import { useState, useEffect } from 'react';
import { Eye, Search, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AdminLayout } from '@/components/AdminLayout';
import { orderApi } from '@/lib/api';
import { toast } from 'sonner';
import ReactDOMServer from "react-dom/server";
import StickerPrint from "../../components/StickerPrint"

const statusOptions = [
  { value: 'pending', label: 'Pending', color: 'bg-yellow-500/20 text-yellow-400' },
  { value: 'processing', label: 'Processing', color: 'bg-blue-500/20 text-blue-400' },
  { value: 'shipped', label: 'Shipped', color: 'bg-purple-500/20 text-purple-400' },
  { value: 'delivered', label: 'Delivered', color: 'bg-green-500/20 text-green-400' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-500/20 text-red-400' },
];

const paymentStatusOptions = [
  { value: 'pending', label: 'Pending', color: 'bg-yellow-500/20 text-yellow-400' },
  { value: 'paid', label: 'Paid', color: 'bg-green-500/20 text-green-400' },
  { value: 'failed', label: 'Failed', color: 'bg-red-500/20 text-red-400' },
];

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    loadOrders();
  }, [statusFilter]);

  const loadOrders = async () => {
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const res = await orderApi.getAll(params);
      setOrders(res.data);
    } catch (error) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };


  const handleStatusChange = async (orderId, newStatus, isPayment = false) => {
    try {
      if (isPayment) {
        await orderApi.updateStatus(orderId, orders.find(o => o.id === orderId).order_status, newStatus);
      } else {
        await orderApi.updateStatus(orderId, newStatus);
      }
      toast.success('Order updated!');
      loadOrders();
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const filteredOrders = orders.filter(
    (o) =>
      o.order_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
      o.customer_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      o.customer_email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusColor = (status, type = 'order') => {
    const options = type === 'payment' ? paymentStatusOptions : statusOptions;
    return options.find((s) => s.value === status)?.color || 'bg-stone-500/20 text-stone-400';
  };

  const printOrderSticker = (order) => {
    const html = ReactDOMServer.renderToStaticMarkup(
      <StickerPrint order={order} />
    );

    const win = window.open("", "", "width=400,height=600");

    win.document.write(`
    <html>
      <head><title>Print</title></head>
      <body>${html}</body>
    </html>
  `);

    win.document.close();
    win.focus();
    win.print();
    win.close();
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Orders</h1>
          <p className="text-stone-400 mt-1">{orders.length} total orders</p>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-500" />
            <Input
              placeholder="Search by order #, name, email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-stone-900 border-stone-700 text-white"
              data-testid="order-search"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 bg-stone-900 border-stone-700 text-white" data-testid="order-status-filter">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-stone-800 border-stone-700">
              <SelectItem value="all" className="text-white">All Status</SelectItem>
              {statusOptions.map((s) => (
                <SelectItem key={s.value} value={s.value} className="text-white">
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Orders Table */}
        <div className="bg-stone-900 border border-stone-800 overflow-x-auto">
          <table className="w-full" data-testid="orders-table">
            <thead>
              <tr className="border-b border-stone-800">
                <th className="text-left p-4 text-stone-400 font-medium">Order</th>
                <th className="text-left p-4 text-stone-400 font-medium">Customer</th>
                <th className="text-left p-4 text-stone-400 font-medium">Date</th>
                <th className="text-left p-4 text-stone-400 font-medium">Total</th>
                <th className="text-left p-4 text-stone-400 font-medium">Payment</th>
                <th className="text-left p-4 text-stone-400 font-medium">Status</th>
                <th className="text-right p-4 text-stone-400 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={3} className="text-center py-8 text-stone-500">Loading...</td>
                </tr>
              ) : filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan={3} className="text-center py-8 text-stone-500">No orders found</td>
                </tr>
              ) : (
                filteredOrders.map((order) => (
                  <tr
                    key={order.id}
                    className="border-b border-stone-800 hover:bg-stone-800/50"
                    data-testid={`order-row-${order.id}`}
                  >
                    <td className="p-4">
                      <p className="font-mono text-emerald-400 font-medium">{order.order_number}</p>
                      <p className="text-xs text-stone-500">{order.items.length} items</p>
                    </td>
                    <td className="p-4">
                      <p className="text-white">{order.customer_name}</p>
                      <p className="text-sm text-stone-500">{order.customer_email}</p>
                    </td>
                    <td className="p-4 text-stone-300 text-sm">{formatDate(order.created_at)}</td>
                    <td className="p-4">
                      <p className="text-white font-bold">KES {order.total_amount.toLocaleString()}</p>
                      <p className="text-xs text-stone-500">{order.payment_method === 'mpesa' ? 'M-Pesa' : 'COD'}</p>
                    </td>
                    <td className="p-4">
                      <Select
                        value={order.payment_status}
                        onValueChange={(val) => handleStatusChange(order.id, val, true)}
                      >
                        <SelectTrigger className="w-28 h-8 bg-transparent border-0 p-0" data-testid={`payment-status-${order.id}`}>
                          <Badge className={getStatusColor(order.payment_status, 'payment')}>
                            {order.payment_status}
                          </Badge>
                        </SelectTrigger>
                        <SelectContent className="bg-stone-800 border-stone-700">
                          {paymentStatusOptions.map((s) => (
                            <SelectItem key={s.value} value={s.value} className="text-white">
                              {s.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-4">
                      <Select
                        value={order.order_status}
                        onValueChange={(val) => handleStatusChange(order.id, val)}
                      >
                        <SelectTrigger className="w-32 h-8 bg-transparent border-0 p-0" data-testid={`order-status-${order.id}`}>
                          <Badge className={getStatusColor(order.order_status)}>
                            {order.order_status}
                          </Badge>
                        </SelectTrigger>
                        <SelectContent className="bg-stone-800 border-stone-700">
                          {statusOptions.map((s) => (
                            <SelectItem key={s.value} value={s.value} className="text-white">
                              {s.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-4">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setSelectedOrder(order)}
                        className="text-stone-400 hover:text-white"
                        data-testid={`view-order-${order.id}`}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Order Detail Dialog */}
        <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
          <DialogContent className="bg-stone-900 border-stone-800 max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-white">
                Order {selectedOrder?.order_number}
              </DialogTitle>
            </DialogHeader>
            {selectedOrder && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-stone-500">Customer</p>
                    <p className="text-white">{selectedOrder.customer_name}</p>
                    <p className="text-stone-400">{selectedOrder.customer_email}</p>
                    <p className="text-stone-400">{selectedOrder.customer_phone}</p>
                  </div>
                  <div>
                    <p className="text-stone-500">Delivery Address</p>
                    <p className="text-white">{selectedOrder.delivery_address}</p>
                  </div>
                </div>

                <div className="border-t border-stone-800 pt-4">
                  <p className="text-stone-500 mb-2">Items</p>
                  {selectedOrder.items.map((item, i) => (
                    <div key={i} className="flex justify-between py-2 border-b border-stone-800 last:border-0">
                      <div>
                        <p className="text-white">{item.product_name}</p>
                        <p className="text-sm text-stone-500">Qty: {item.quantity}</p>
                      </div>
                      <p className="text-emerald-400">KES {item.total_price.toLocaleString()}</p>
                      {(item.size || item.color) && (
                        <p className="text-stone-400">
                          {item.size && <span>Size: {item.size}</span>}
                          {item.size && item.color && <span className="mx-1">|</span>}
                          {item.color && <span>{item.color}</span>}
                        </p>
                      )}
                    </div>
                  ))}
                </div>

                <div className="border-t border-stone-800 pt-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-stone-500">Subtotal</span>
                    <span className="text-white">KES {selectedOrder.subtotal.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-stone-500">Total Saved</span>
                    <span className="text-white">KES {selectedOrder.tax.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-stone-500">Shipping</span>
                    <span className="text-white">KES {selectedOrder.shipping_cost.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold pt-2 border-t border-stone-800">
                    <span className="text-white">Total</span>
                    <span className="text-emerald-400">KES {selectedOrder.total_amount.toLocaleString()}</span>
                  </div>
                </div>

                <Button
                  className="mt-4 bg-emerald-600 text-white px-4 py-2 rounded hover:bg-emerald-700"
                  onClick={() => printOrderSticker(selectedOrder)}
                >
                  Print Order Sticker
                </Button>

                {/* <div style={{ position: "absolute", left: "-10000px", top: 0 }}>
                  <StickerPrint order={selectedOrder} />
                </div> */}

                <button
                  className="mt-4 bg-emerald-600 text-white px-4 py-2 rounded hover:bg-emerald-700"
                  onClick={() => window.print()}
                >
                  Print Sticker
                </button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
